import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  stockConsultantApiService,
  StockProductsResponse,
  StockProductsParams,
} from '../../../../core/modules/stockConsultant/services/api';
import apiService from '../../../../core/services/api-client';

interface AppliedFilters {
  product: string | null;
  sku: string | null;
  warehouse: string | null;
  supplier: string | null;
}

interface AvailableFilters {
  warehouses: string[];
  suppliers: string[];
  skus: string[];
  products: string[];
}

const ITEMS_PER_PAGE = 50;

const StockConsultantPage = () => {
  const { id: companyId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<StockProductsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [isDownloading, setIsDownloading] = useState(false);

  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    product: null,
    sku: null,
    warehouse: null,
    supplier: null,
  });

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.trunc(amount));
  };

  const fetchFilters = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await stockConsultantApiService.getFilters(companyId);
      setAvailableFilters(response.filters);
    } catch (err) {
      console.error('Error al obtener filtros:', err);
    }
  }, [companyId]);

  const fetchProducts = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);

      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      const params: StockProductsParams = {
        limit: ITEMS_PER_PAGE,
        offset,
        sort_order: sortOrder,
      };

      if (appliedFilters.product) params.filter_product = appliedFilters.product;
      if (appliedFilters.sku) params.filter_sku = appliedFilters.sku;
      if (appliedFilters.warehouse) params.filter_warehouse = appliedFilters.warehouse;
      if (appliedFilters.supplier) params.filter_supplier = appliedFilters.supplier;

      const response = await stockConsultantApiService.getProducts(companyId, params);
      setData(response);
    } catch (err: any) {
      console.error('Error al obtener productos:', err);
      setError(err.response?.data?.detail || err.message || 'Error al obtener productos');
    } finally {
      setLoading(false);
    }
  }, [companyId, currentPage, sortOrder, appliedFilters]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Resetear página al cambiar filtros u orden
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters, sortOrder]);

  const handleFilterChange = (filterName: keyof AppliedFilters, value: string | null) => {
    setAppliedFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const totalItems = data?.total ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const handleDownloadExcel = async () => {
    if (!companyId) return;
    setIsDownloading(true);
    try {
      const params: any = { sort_order: sortOrder };
      if (appliedFilters.product) params.filter_product = appliedFilters.product;
      if (appliedFilters.sku) params.filter_sku = appliedFilters.sku;
      if (appliedFilters.warehouse) params.filter_warehouse = appliedFilters.warehouse;
      if (appliedFilters.supplier) params.filter_supplier = appliedFilters.supplier;

      const response = await apiService().get(
        `/stock-consultant/${companyId}/export`,
        { params, responseType: 'blob' }
      );

      const blob = response.data;
      if (blob.size === 0) throw new Error('El archivo descargado está vacío');

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'consultor_stock.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error al descargar Excel:', err);
      alert(`Error al descargar: ${err.message || 'Error desconocido'}`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <div className="w-full p-24">
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/dashboard/metrics/${companyId}`)}
            sx={{ mr: 2, color: '#000000' }}
          >
            Volver
          </Button>
          <Typography variant="h4">Error al cargar Consultor de Stock</Typography>
        </Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchProducts}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="w-full p-24">
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/dashboard/metrics/${companyId}`)}
            sx={{ mr: 2, color: '#000000' }}
          >
            Volver
          </Button>
          <Typography variant="h4">
            Consultor de Stock ({data?.total_products ?? 0} productos)
          </Typography>
        </Box>
        <Tooltip title={isDownloading ? 'Descargando...' : 'Descargar Excel'}>
          <IconButton
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            sx={{
              backgroundColor: '#000000',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': { backgroundColor: '#333333' },
              '&:disabled': { backgroundColor: '#9ca3af', color: 'white' },
            }}
          >
            {isDownloading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filtros */}
      {availableFilters && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
            Filtros
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              options={availableFilters.products}
              value={appliedFilters.product}
              onChange={(_, value) => handleFilterChange('product', value)}
              renderInput={(params) => <TextField {...params} label="Producto" size="small" />}
              sx={{ minWidth: 250 }}
            />
            <Autocomplete
              options={availableFilters.skus}
              value={appliedFilters.sku}
              onChange={(_, value) => handleFilterChange('sku', value)}
              renderInput={(params) => <TextField {...params} label="SKU" size="small" />}
              sx={{ minWidth: 200 }}
            />
            <Autocomplete
              options={availableFilters.warehouses}
              value={appliedFilters.warehouse}
              onChange={(_, value) => handleFilterChange('warehouse', value)}
              renderInput={(params) => <TextField {...params} label="Bodega" size="small" />}
              sx={{ minWidth: 200 }}
            />
            {availableFilters.suppliers && availableFilters.suppliers.length > 0 && (
              <Autocomplete
                options={availableFilters.suppliers}
                value={appliedFilters.supplier}
                onChange={(_, value) => handleFilterChange('supplier', value)}
                renderInput={(params) => <TextField {...params} label="Proveedor" size="small" />}
                sx={{ minWidth: 200 }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Cuadros de totales */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total de Productos</Typography>
              <Typography variant="h3" color="primary">
                {(data?.total_products ?? 0).toLocaleString('es-CL')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total de Unidades</Typography>
              <Typography variant="h3" color="secondary">
                {(data?.total_units ?? 0).toLocaleString('es-CL')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Valor Total</Typography>
              <Typography variant="h3" color="success.main">
                {formatCurrency(data?.total_value ?? 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botón de ordenamiento */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body1" sx={{ color: '#374151' }}>Ordenar por stock:</Typography>
        <ToggleButtonGroup
          value={sortOrder}
          exclusive
          onChange={(_, value) => { if (value) setSortOrder(value); }}
          size="small"
        >
          <ToggleButton value="desc">Mayor a menor</ToggleButton>
          <ToggleButton value="asc">Menor a mayor</ToggleButton>
        </ToggleButtonGroup>
        {loading && <CircularProgress size={20} />}
      </Box>

      {/* Tabla de productos */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Bodega</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="right">Costo</TableCell>
                  <TableCell align="right">Días en Alerta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!data?.products || data.products.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No hay productos para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.products.map((product, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {product.product_sku}
                        </Typography>
                      </TableCell>
                      <TableCell>{product.product_name}</TableCell>
                      <TableCell>{product.supplier ?? '-'}</TableCell>
                      <TableCell>{product.warehouse}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {product.stock.toLocaleString('es-CL')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(product.cost ?? 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {product.alert_days === 0 ? (
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>-</Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              color: product.alert_days > 30
                                ? '#dc2626'
                                : product.alert_days > 7
                                  ? '#ea580c'
                                  : '#d97706',
                            }}
                          >
                            {product.alert_days} días
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          {totalItems > ITEMS_PER_PAGE && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems} productos
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <Typography variant="body2" sx={{ mx: 2 }}>
                  Página {currentPage} de {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StockConsultantPage;
